/**
 * Bottom action bar (DESIGN.md §8): running total left, THE single primary
 * right (≥56px). Fixed to the content pane; screens rendered with the bar
 * reserve its height (.screen-pane--with-bar) so occlusion is impossible
 * by construction (the R14 hidden-CTA bug class).
 *
 * ⭐ T1749 M4 — THE BAR ITSELF IS `@ecmb/ui`'s `Bar`, in its `default` variant,
 * and this file is the ADAPTER that formats the money. Its markup, its classes
 * and its live region are unchanged byte for byte; the eight call sites are
 * untouched.
 *
 * ⛔ AN ADAPTER RATHER THAN A BARE RE-EXPORT, AND THE REASON IS THE ONE RULE
 * THIS PACKAGE CANNOT BREAK: no money in `@ecmb/ui`. `Bar` takes an
 * ALREADY-FORMATTED figure, because formatting currency belongs to the tool
 * that knows the account, the rounding and the words — so the `totalCents ->
 * euro()` hop has to happen on this side of the boundary. Two lines, and they
 * are the two lines that keep a shared presentational package from being the
 * second place money is rendered.
 */
import { Bar, type BarVariant, type BarQuietControl } from '@ecmb/ui';
import { euro } from '../lib/format.js';

export type TotalBarProps = {
  /**
   * ⭐ T1749 M8 (R7) — THE VARIANT PASSES THROUGH, AND THE MONEY HOP STAYS ON
   * THIS SIDE. Every caller but Quote keeps `default`; the Quote screen mounts
   * `quote` (`Accept` / `Record agreed price` on the fixed bar, the journey line
   * with the customer's name and phone, `Save for later` / `Override price` quiet
   * above). The `quote`-only fields below are ignored by `Bar`'s `default` branch,
   * so the eight `default` call sites are untouched.
   */
  variant?: BarVariant;
  label: string;
  totalCents: number | null;
  primaryLabel: string;
  primaryDisabled?: boolean;
  /**
   * T660 M6 P1-5 — a re-price is in flight. Screen-specific, so it arrives as a
   * prop rather than being read from the store in here: the bar is shared by
   * Location (no re-price at all), Add-ons and Quote.
   */
  pending?: boolean;
  onPrimary: () => void;
  /** `quote` only — the customer's name on the journey line. */
  customerName?: string;
  /** `quote` only — the customer's phone, beside the name (the phone never
   *  closes: the operator looking at a price is the one who may ring back). */
  customerPhone?: string;
  /** `quote` only — the quiet controls in the row above the bar. */
  quiet?: readonly BarQuietControl[];
};

export function TotalBar({
  variant = 'default', label, totalCents, primaryLabel, primaryDisabled, pending, onPrimary,
  customerName, customerPhone, quiet,
}: TotalBarProps) {
  return (
    <Bar
      variant={variant}
      label={label}
      /* K5-2 (DESIGN.md §8): NO FIGURE until a price exists. The bar used to
         render a literal ellipsis for null, so a bar that announces a running
         total showed a placeholder Helen could read as a value that failed to
         load. `null` reaches `Bar` as null and renders nothing; the label stays,
         because it says what the bar is FOR.
         ⭐⭐ T1749 M8 round 2 (webapp Flow 2) — AND A RE-PRICE IN FLIGHT SAYS SO
         IN THE SLOT, which is the state the milestone's busy criterion is about.
         Measured live: mid-flight the bar rendered `<div class="total-bar-info"
         aria-busy="true"><span class="total-bar-label">Total</span></div>` — the
         money was not dimmed, it was ABSENT, so the one figure Helen watches
         simply vanished for the duration (§7.1: no unexplained moment).
         ⛔ NOT A DIMMED FIGURE, EITHER: a stale euro amount at reduced opacity is
         the defect Captain's M3 item 8 retired (opacity dims the ink, and the
         number would be the PREVIOUS band's). The word is the honest content.
         ⛔ AND NO MONEY CROSSES INTO `@ecmb/ui`: `figure` is already a formatted
         STRING, so this is one more string through the same prop (§11's rule that
         the package holds no currency). The word matches the one the line items
         already use for the same state, so the screen says it once, twice. */
      figure={pending === true ? 'Updating…' : totalCents === null ? null : euro(totalCents)}
      primaryLabel={primaryLabel}
      primaryDisabled={primaryDisabled}
      pending={pending}
      onPrimary={onPrimary}
      customerName={customerName}
      customerPhone={customerPhone}
      quiet={quiet}
    />
  );
}
